import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, of, timer } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { API_BASE_URL } from './api-url.token';

/**
 * Possible PoC tier values returned by the backend.
 * `null` means "not a PoC tenant" — the bar must be hidden.
 */
export type PocTier = 'tier_1' | 'tier_2' | null;

interface TierResponse {
  tier: PocTier;
}

/**
 * Polls the backend's `/api/TenantTier/getTier` endpoint every 60s and exposes
 * the result as a BehaviorSubject for the header bar to subscribe to. The
 * backend resolves the current tenant from the Origin header and proxies the
 * Tenant Registry — paying customers (Tier 3) are not in the registry and so
 * receive `tier: null`, which the header treats as "hide the bar".
 */
@Injectable({
  providedIn: 'root',
})
export class TenantTierService {
  private static readonly POLL_INTERVAL_MS = 60_000;

  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_BASE_URL);

  private readonly tierSubject = new BehaviorSubject<PocTier>(null);
  private pollSubscription: Subscription | null = null;

  /**
   * Latest known tier. Emits `null` until the first poll resolves.
   * Components binding to this should treat `null` as "hide the bar".
   */
  public readonly tier$: Observable<PocTier> = this.tierSubject.asObservable();

  /**
   * Starts the 60s poll loop. Safe to call multiple times — re-entrant calls
   * are ignored so callers don't need to track lifecycle themselves.
   */
  startPolling(): void {
    if (this.pollSubscription) {
      return;
    }
    this.pollSubscription = timer(0, TenantTierService.POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.fetchTier()),
        tap((tier) => this.tierSubject.next(tier)),
      )
      .subscribe();
  }

  /**
   * Stops the poll loop. Call on logout or app teardown if you don't want
   * unnecessary requests firing.
   */
  stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = null;
    }
  }

  private fetchTier(): Observable<PocTier> {
    return this.http.get<TierResponse>(`${this.apiUrl}TenantTier/getTier`).pipe(
      // Swallow transient errors — a flickering bar on a 5xx is worse than
      // a stale value. Keep the last good tier.
      catchError(() => of({ tier: this.tierSubject.value } as TierResponse)),
      map((r) => (r ? r.tier : null)),
    );
  }
}
